/**
 * Data Quality Badge Component
 * Migrated to use cambrian-ds Chip and Spinner with design tokens
 */

import { Chip, Spinner } from "cambrian-ds";
import type { EnrichmentQuality } from '../lib/deep42';
import { formatQualityScore, getQualityBadgeColor } from '../lib/deep42';
import { cn } from "@/lib/utils";
import { cambrianColors } from "@/styles/design-tokens";

interface DataQualityBadgeProps {
  quality: EnrichmentQuality | null;
  isEnriching: boolean;
}

export function DataQualityBadge({ quality, isEnriching }: DataQualityBadgeProps) {
  if (isEnriching) {
    return (
      <Chip
        variant="flat"
        size="sm"
        className={cn(
          cambrianColors.status.info.bg,
          cambrianColors.status.info.text,
          "border border-blue-200"
        )}
      >
        <span className="flex items-center gap-2">
          <Spinner size="sm" />
          <span>Enriching data...</span>
        </span>
      </Chip>
    );
  }

  if (!quality) {
    return null;
  }

  const completenessColor = getQualityBadgeColor(quality.metadataCompleteness, 'completeness');
  const legitimacyColor = getQualityBadgeColor(quality.legitimacyScore, 'legitimacy');

  return (
    <div className="flex items-center gap-2">
      {/* Metadata Completeness */}
      <Chip
        variant="flat"
        size="sm"
        className={completenessColor}
        title="Metadata completeness (website, social links, contracts, description)"
      >
        {formatQualityScore(quality.metadataCompleteness, 'percentage')} Complete
      </Chip>

      {/* Legitimacy Score */}
      {quality.legitimacyScore > 0 && (
        <Chip
          variant="flat"
          size="sm"
          className={legitimacyColor}
          title="12-point legitimacy verification score"
        >
          {formatQualityScore(quality.legitimacyScore, 'percentage')} Verified
        </Chip>
      )}

      {/* High Quality Indicator */}
      {quality.isHighQuality && (
        <Chip
          variant="flat"
          size="sm"
          className={cn(
            cambrianColors.sentiment.positive.bg,
            cambrianColors.sentiment.positive.text,
            cambrianColors.sentiment.positive.border,
            "border"
          )}
          title="High-quality project (80+ legitimacy, 95%+ confidence)"
        >
          <span className="flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span>High Quality</span>
          </span>
        </Chip>
      )}
    </div>
  );
}
